import type { CraterOperation, GameState, PlayerState, ProjectileResult, ShotResult } from '../shared/types';
import { turnKey } from './turn-transition';
import { cameraTurnPlayerId, sourcePlayerFocus } from './battle-camera';
import { Terrain } from '../shared/terrain';
import { terrainBodyAngle, sourceBodyPoint } from '../shared/shot-geometry';
import { MovementPresentation } from './movement';
import { CharacterAnimation, attackPresentationEnd, volleyLaunches, type CharacterAction } from './character-animation';
import { BattleAssetLoader, collectBattleAssets, roomAssetKey } from './battle-asset-loader';
import classicAim from '../shared/classic-aim.json';
import attackAssetData from '../shared/character-attack-assets.json';
import feedbackAssets from '../shared/character-feedback-assets.json';
import deathAssets from '../shared/character-death-assets.json';
import { DeathPresentation, deathEventAt, deathLoopFrame, type DeathFrame } from './death-presentation';
import { combatPlayers, damageEventAt, type PresentedPlayer } from './combat-presentation';
import { bindAssetImage, drawLogicalImage, logicalImageSize } from './hd-assets';
import { sourceFrameBlend, type SourceFrameBlend } from './source-frame-blend';
import { InterpolatedSprite } from './interpolated-sprite';
import { projectileHeading } from './projectile-heading';
import { backgroundViewport } from './background-viewport';
import { projectilePresentationElapsed, type FirstVolleyClock } from './shot-presentation-clock';

export interface MapAsset {
  id: string | number; name: string; width: number; height: number;
  background: string; foreground: string; mask?: string;
  [key: string]: unknown;
}
export interface BulletAsset {
  projectile: string[]; explosion: string[]; craterMask?: string; crater?: string;
  spin?: boolean | number; [key: string]: unknown;
}
export interface WeaponAsset {
  id: string | number; name: string; icon: string; held: string;
  minAngle: number; maxAngle: number; normal: BulletAsset; special: BulletAsset;
  heldSheet?: string; heldAnchor?: { x: number; y: number };
  [key: string]: unknown;
}
export interface CharacterAsset {
  id: string | number; name: string; idle: string | string[]; walk: string[]; shot: string[];
  throw?: string[]; showgun?: string[]; showthrows?: string[];
  hidegun?: string[]; hidethrows?: string[];
  handclip?: string[]; hit?: string[]; cry?: string[];
  width: number; height: number; anchor?: { x: number; y: number };
  weaponAnchor?: { x: number; y: number };
  frameRate?: number; sheetColumns?: number; sheetRows?: number;
  animationTimelines?: Partial<Record<CharacterAction, { frameRate?: number; sourceFrames?: number[] }>>;
  roomPortrait?: string; roomPortraitWidth?: number; roomPortraitHeight?: number;
}
export interface GameAssets {
  maps: MapAsset[]; weapons: WeaponAsset[]; characters: CharacterAsset[];
  ui?: Record<string, string>;
  sounds?: Record<string, string>; actions?: Partial<Record<'fly' | 'ice', BulletAsset>>; [key: string]: unknown;
  props?: { id: string; icon: string }[];
}
interface Point { x: number; y: number; t?: number }
interface RenderProjectile extends ProjectileResult { weapon: WeaponAsset; special: boolean; localClock?: FirstVolleyClock }
interface ShotAnimation { id: string; started: number; duration: number; bullets: RenderProjectile[]; previous: GameState | null; result: ShotResult; historyCraters: CraterOperation[] }
interface Camera { x: number; y: number; scale: number; zoom: number; manual: boolean; fullMap: boolean }
interface CameraTransition { playerId: string; startsAt: number; duration: number; from: Point | null }
interface WeaponMovieAsset {
  frames: string[]; width: number; height: number; anchor: Point; frameRate: number; position: Point;
  phases: Record<'start' | 'shot' | 'end', { frames: string[]; sourceFrames: number[]; durationMs: number }>;
}
const attackAssets = attackAssetData as unknown as {
  weapons: Record<string, { normal: WeaponMovieAsset; special: WeaponMovieAsset }>;
  actions: Record<'fly' | 'ice', WeaponMovieAsset>;
};

export class BattleRenderer {
  private ctx: CanvasRenderingContext2D;
  private miniCtx: CanvasRenderingContext2D;
  private images = new Map<string, HTMLImageElement>();
  private masks = new Map<string, Promise<HTMLCanvasElement>>();
  private collisionBases = new Map<string, Promise<Terrain>>();
  private collisionMasks = new Map<string, Terrain>();
  private collisionVersion = '';
  private collisionTerrain: Terrain | null = null;
  private movement = new MovementPresentation();
  private characterAnimation = new CharacterAnimation();
  private spriteBlend = new InterpolatedSprite();
  private characterFrames = new Map<string, { action: CharacterAction; frameIndex: number; sourceFrame: number; key: string;
    nextFrameIndex: number; nextSourceFrame: number; mix: number; startedAt: number; weaponPhaseElapsed: number;
    bodyAngle: number; muzzle: Point; pose: Point; falling: boolean;
    weaponId: string | null; kind: ShotResult['kind'] | null;
    weaponMovie: { path: string; nextPath: string; phase: 'start' | 'shot' | 'end'; frameIndex: number; nextFrameIndex: number; mix: number; sourceFrame: number; loaded: boolean } | null }>();
  private shownDamage = new Set<string>();
  private shownImpacts = new Set<string>();
  private propEffects: { playerId: string; startedAt: number; icon?: string }[] = [];
  private feedbackFrames = new Map<string, { propAura: number[]; powReadyAura: number | null; frozenBlock: number | null;
    interpolation: { propAura: SourceFrameBlend[]; powReadyAura: SourceFrameBlend | null; frozenBlock: SourceFrameBlend | null } }>();
  private powClocks = new Map<string, number>();
  private frozenClocks = new Map<string, number>();
  private deaths = new DeathPresentation();
  private deathFrames = new Map<string, DeathFrame & { characterId: string; soulPath: string; ghostFrame: number; shineFrame: number }>();
  private state: GameState | null = null;
  private stateNeedsPhysicsFrame = false;
  private assets: GameAssets;
  private assetLoader: BattleAssetLoader;
  private coreAssetKey = '';
  private terrain = document.createElement('canvas');
  private terrainVersion = '';
  private terrainReadyVersion = '';
  private visibleCraterCount = 0;
  private coreReady = false;
  private coreGeneration = 0;
  private pendingCoreImages = new Set<string>();
  private frame = 0;
  private map: MapAsset | null = null;
  private camera: Camera = { x: 0, y: 0, scale: 1, zoom: 1, manual: false, fullMap: false };
  private cameraTransition: CameraTransition | null = null;
  private lastCameraFrame = 0;
  private presentedTurnId = '';
  private reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  private shot: ShotAnimation | null = null;
  private renderedShots = new Set<string>();
  private walking = new Map<string, number>();
  private aim: { angle: number; playerId: string; from: number; startedAt: number } | null = null;
  private miniBase = document.createElement('canvas');
  private miniBaseMap: MapAsset | null = null;
  private miniBaseBackground: HTMLImageElement | null = null;
  private miniBaseTerrain: HTMLCanvasElement | null = null;
  private miniBaseTerrainVersion = '';
  private miniBaseBuilds = 0;
  private lastMinimapFrame = 0;
  private clockOffset = 0;
  private resizeObserver: ResizeObserver;
  loading = true;
  onAssetReady?: () => void;
  onExplosion?: () => void;

  constructor(private canvas: HTMLCanvasElement, private minimap: HTMLCanvasElement, assets: GameAssets, private localPlayerId = '', assetLoader?: BattleAssetLoader) {
    this.ctx = canvas.getContext('2d')!;
    this.miniCtx = minimap.getContext('2d')!;
    this.assetLoader = assetLoader ?? new BattleAssetLoader(assets);
    this.assets = this.assetLoader.assets;
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    window.addEventListener('resize', this.handleResize);
    this.resize();
    this.animate = this.animate.bind(this);
    this.frame = requestAnimationFrame(this.animate);
    minimap.addEventListener('pointerdown', this.handleMapPointer);
    canvas.addEventListener('wheel', this.handleWorldWheel, { passive: false });
  }

  private handleResize = () => this.resize();
  private handleMapPointer = (event: PointerEvent) => {
    if (!this.map) return;
    const rect = this.minimap.getBoundingClientRect();
    this.camera.x = ((event.clientX - rect.left) / rect.width) * this.map.width;
    this.camera.y = ((event.clientY - rect.top) / rect.height) * this.map.height;
    this.camera.manual = true;
    this.cameraTransition = null;
  };
  private handleWorldWheel = (event: WheelEvent) => {
    event.preventDefault();
    this.zoom(event.deltaY < 0 ? 0.15 : -0.15);
  };

  private image(path?: string): HTMLImageElement | null {
    if (!path) return null;
    const decoded = this.assetLoader.getImage(path);
    if (decoded) { this.images.set(path, decoded); return decoded; }
    let img = this.images.get(path);
    if (!img) {
      img = new Image();
      bindAssetImage(img, path);
      this.images.set(path, img);
      img.onload = () => this.onAssetReady?.();
    }
    return img.complete && img.naturalWidth > 0 ? img : null;
  }

  private async loadImage(path: string): Promise<HTMLImageElement | null> {
    const image = await this.assetLoader.imageReady(path);
    if (image) this.images.set(path, image);
    return image;
  }

  private updateLoading() {
    this.loading = !this.coreReady || !this.terrainReadyVersion;
    this.onAssetReady?.();
  }

  private async prepareCoreImages(state: GameState, map: MapAsset) {
    const generation = ++this.coreGeneration;
    this.coreReady = false; this.loading = true;
    this.pendingCoreImages = new Set(this.assetLoader.statusFor(state).pending);
    this.updateLoading();
    const ready = await this.assetLoader.warm(state);
    if (generation !== this.coreGeneration) return;
    const plan = collectBattleAssets(this.assets, state);
    for (const path of plan.images) {
      const image = this.assetLoader.getImage(path);
      if (image) this.images.set(path, image);
    }
    for (const path of plan.masks) {
      const terrain = this.assetLoader.getTerrain(path);
      if (terrain) {
        this.collisionMasks.set(path, terrain);
        if (path === map.mask) this.collisionBases.set(path, Promise.resolve(terrain));
      }
    }
    this.pendingCoreImages = new Set(this.assetLoader.statusFor(state).pending);
    this.coreReady = ready;
    this.updateLoading();
    if (ready) { this.preloadEffects(); void this.prepareTerrain(); }
  }

  private preloadEffects() {
    if (!this.state) return;
    for (const player of this.state.players) {
      const character = this.assets.characters[player.team] ?? this.assets.characters[0];
      if (character) for (const path of [...Array.isArray(character.idle) ? character.idle : [character.idle], ...character.walk, ...character.shot, ...character.throw ?? [], ...character.showgun ?? [], ...character.showthrows ?? []]) this.image(path);
      const weapon = this.assets.weapons.find((w) => String(w.id) === String(player.weaponId));
      if (weapon) for (const ball of [weapon.normal, weapon.special]) {
        for (const path of ball.projectile) this.image(path);
        for (const path of ball.explosion) this.image(path);
        if (ball.crater) this.image(ball.crater);
        if (ball.craterMask) void this.binaryMask(ball.craterMask).catch(() => {});
      }
    }
    for (const action of Object.values(this.assets.actions ?? {})) if (action) {
      for (const path of action.projectile) this.image(path);
      for (const path of action.explosion) this.image(path);
    }
  }

  resize() {
    // The classic stage uses logical CSS pixels and may be transformed as a
    // whole. A transformed DOMRect would apply its visual scale twice.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
    const height = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
    const miniWidth = Math.max(1, Math.round(this.minimap.clientWidth * dpr));
    const miniHeight = Math.max(1, Math.round(this.minimap.clientHeight * dpr));
    // Canvas dimension setters clear its bitmap even when the value matches.
    // Repeated observer notifications must preserve the last complete frame.
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    if (this.minimap.width !== miniWidth) this.minimap.width = miniWidth;
    if (this.minimap.height !== miniHeight) this.minimap.height = miniHeight;
  }

  setState(state: GameState, shot?: ShotResult, serverTime?: number, ackSeq?: number) {
    if (serverTime) this.clockOffset = serverTime - Date.now();
    const previous = this.state;
    this.state = state;
    // Death tracks and impact restarts are created in draw. A late finished
    // snapshot still needs that pass before GameOverAction may see a settled map.
    this.stateNeedsPhysicsFrame = true;
    this.movement.observe(state, ackSeq, performance.now(), Date.now() + this.clockOffset);
    if (previous && previous.round !== state.round) { this.movement.clearTerrain(); this.deaths.reset(); this.deathFrames.clear(); }
    const nextMap = this.assets.maps.find((entry) => String(entry.id) === String(state.mapId)) ?? this.assets.maps[0];
    if (nextMap && String(nextMap.id) !== String(this.map?.id)) {
      this.map = nextMap;
      const active = state.players.find((p) => p.id === state.turn.playerId);
      this.camera.x = active?.x ?? nextMap.width / 2;
      this.camera.y = active ? sourcePlayerFocus(active).y : nextMap.height / 2;
      this.camera.zoom = 1;
      this.camera.fullMap = false;
      this.camera.manual = false;
      this.cameraTransition = null;
      this.presentedTurnId = shot && Date.now() + this.clockOffset < state.turn.readyAt ? shot.playerId : state.turn.playerId;
      this.collisionVersion = '';
      this.collisionTerrain = null;
      this.movement.clearTerrain();
      this.terrainVersion = '';
      this.terrainReadyVersion = '';
      this.shot = null;
      this.renderedShots.clear();
      this.characterAnimation.reset(); this.characterFrames.clear();
      this.shownDamage.clear(); this.shownImpacts.clear(); this.propEffects = []; this.feedbackFrames.clear();
      this.powClocks.clear(); this.frozenClocks.clear();
      this.deaths.reset(); this.deathFrames.clear();
    }
    const assetKey = roomAssetKey(state);
    if (nextMap && assetKey !== this.coreAssetKey) { this.coreAssetKey = assetKey; void this.prepareCoreImages(state, nextMap); }
    if (previous) {
      for (const player of state.players) {
        const old = previous.players.find((candidate) => candidate.id === player.id);
        if (!shot && old && Math.abs(old.x - player.x) > 1) this.walking.set(player.id, performance.now() + 250);
      }
      if (state.phase === 'playing' && turnKey(previous) !== turnKey(state)) {
        this.characterAnimation.cancelPreparation();
        // Network state already contains the next turn while the old shot is
        // flying. Hold its visual focus until authoritative settling finishes.
        const startsAt = performance.now() + state.turn.readyAt - (Date.now() + this.clockOffset);
        this.cameraTransition = {
          playerId: state.turn.playerId, startsAt,
          duration: this.reducedMotion ? 180 : 620, from: null,
        };
      }
    }
    if (shot) this.startShot(shot, previous);
    void this.prepareTerrain();
  }

  predictMove(playerId: string, dx: number, seq: number) { this.movement.predict(playerId, dx, seq); }
  cancelPrediction(playerId?: string) { this.movement.cancel(playerId); this.characterAnimation.cancelPreview(playerId); }
  prepareShot(playerId: string, kind: ShotResult['kind'] = 'normal') {
    const player = this.state?.players.find((entry) => entry.id === playerId);
    if (player) this.characterAnimation.prepare(playerId, player.weaponId, kind);
  }
  cancelShotPreparation(playerId?: string) { this.characterAnimation.cancelPreparation(playerId); }
  previewShot(playerId: string, kind: ShotResult['kind'] = 'normal') {
    const player = this.state?.players.find((entry) => entry.id === playerId);
    if (player) this.characterAnimation.preview(playerId, player.weaponId, kind);
  }
  useProp(playerId: string, propId?: string) {
    const now = performance.now();
    this.characterAnimation.useItem(playerId, now);
    const icon = propId === 'pow' ? this.assets.ui?.powIcon : this.assets.props?.find((prop) => prop.id === propId)?.icon;
    this.propEffects.push({ playerId, startedAt: now, icon });
  }
  setAim(playerId: string, angle: number) {
    if (this.aim?.playerId === playerId && this.aim.angle === angle) return;
    const now = performance.now(), player = this.state?.players.find((entry) => entry.id === playerId);
    const from = this.aim?.playerId === playerId ? this.aimAngleAt(now) : player?.angle ?? angle;
    this.aim = { playerId, angle, from, startedAt: now };
  }
  private aimAngleAt(now: number): number {
    if (!this.aim) return 0;
    const fraction = Math.max(0, Math.min(1, (now - this.aim.startedAt) / 40));
    return this.aim.from + (this.aim.angle - this.aim.from) * fraction;
  }
  clearAim() { this.aim = null; }
  zoom(delta: number) { this.camera.fullMap = false; this.camera.zoom = Math.max(.65, Math.min(2.4, this.camera.zoom + delta)); }
  resetCamera() { this.camera.zoom = 1; this.camera.fullMap = true; this.camera.manual = false; this.cameraTransition = null; }
  followTurn() {
    this.camera.fullMap = false; this.camera.manual = false; this.camera.zoom = 1;
    if (this.state) this.cameraTransition = {
      playerId: this.state.turn.playerId,
      startsAt: Math.max(performance.now(), performance.now() + this.state.turn.readyAt - (Date.now() + this.clockOffset)),
      duration: this.reducedMotion ? 180 : 420, from: null,
    };
  }

  get debug() {
    return { mapId: this.map?.id, loading: this.loading, terrainVersion: this.terrainReadyVersion, visibleCraterCount: this.visibleCraterCount, coreReady: this.coreReady, pendingImages: [...this.pendingCoreImages],
      camera: { ...this.camera }, aim: this.aim ? { ...this.aim } : null, presentedTurnId: this.presentedTurnId,
      lastRenderedAt: this.lastCameraFrame, visualAimAngle: this.aim ? this.aimAngleAt(performance.now()) : null,
      minimap: { renderedAt: this.lastMinimapFrame, staticBuilds: this.miniBaseBuilds },
      turnTransition: this.cameraTransition ? { playerId: this.cameraTransition.playerId, startsAt: this.cameraTransition.startsAt, duration: this.cameraTransition.duration } : null,
      movement: this.movement.debug,
      walkingPlayerIds: this.walkingPlayerIds,
      characterFrames: Object.fromEntries(this.characterFrames),
      feedbackFrames: Object.fromEntries(this.feedbackFrames),
      deathFrames: Object.fromEntries(this.deathFrames),
      shotPlayback: this.shotPlayback,
      presentedPlayers: this.presentedPlayers,
      animating: this.animating, loadedImages: this.images.size };
  }

  get animating(): boolean {
    return this.movement.anyFalling() || this.characterAnimation.awaitingShot
      || Boolean(this.shot && performance.now() - this.shot.started < this.shot.result.duration);
  }
  get falling(): boolean { return this.movement.isFalling(this.localPlayerId); }
  /** GameOverAction also waits for client-only physical objects such as tombs. */
  get resultAnimating(): boolean {
    return Boolean(this.state?.phase === 'finished' && this.stateNeedsPhysicsFrame)
      || this.animating || this.deaths.anyMoving;
  }

  get presentedPlayers(): PresentedPlayer[] {
    return this.presentationAt(performance.now());
  }

  playerBodyAngle(playerId: string): number {
    const player = this.presentedPlayers.find((entry) => entry.id === playerId);
    if (!player || !this.collisionTerrain) return 0;
    const movement = this.movement.sample(player.id, performance.now());
    return terrainBodyAngle(this.collisionTerrain, movement ? { ...player, x: movement.x, y: movement.y } : player);
  }

  get shotPlayback() {
    if (!this.shot) return null;
    const elapsed = performance.now() - this.shot.started;
    return { id: this.shot.id, startedAt: this.shot.started, elapsed,
      bodyAngle: this.shot.result.bodyAngle ?? null,
      localPreviewStart: this.characterAnimation.previewStartedAt(this.shot.result.playerId),
      projectiles: this.shot.bullets.map((bullet) => {
        const impactAt = bullet.points.at(-1)?.t ?? bullet.launchDelay;
        const visualElapsed = projectilePresentationElapsed(elapsed, bullet.launchDelay, impactAt, bullet.localClock);
        const active = visualElapsed >= bullet.launchDelay && elapsed < impactAt;
        const point = bullet.points[0];
        const spec = this.shotBall(this.shot!.result.kind, bullet.weapon, bullet.special);
        const timeline = (active ? spec.projectileTimeline : spec.explosionTimeline) as { frameRate?: number; loop?: boolean } | undefined;
        const sprite = visualElapsed >= bullet.launchDelay && (active || bullet.impact)
          ? sourceFrameBlend((active ? visualElapsed : elapsed) - (active ? bullet.launchDelay : impactAt), timeline?.frameRate ?? 25,
            active ? bullet.projectile.length : bullet.explosion.length,
            active && Boolean(timeline?.loop || String(bullet.weapon.id) === 'electricbar')) : null;
        return { index: bullet.index, launchDelay: bullet.launchDelay, impactAt, active, visualElapsed, localClock: bullet.localClock ?? null,
          sprite,
          origin: point ? { x: point.x, y: point.y } : null, position: active ? this.sample(bullet, visualElapsed) : null };
      }) };
  }

  get walkingPlayerIds(): string[] {
    const now = performance.now();
    if (this.loading || this.state?.phase !== 'playing' || (this.shot && now - this.shot.started < this.shot.result.duration)) return [];
    return this.presentationAt(now).filter((player) => player.alive && player.renderAlive
      && this.characterFrames.get(player.id)?.action === 'walk'
      && ((this.walking.get(player.id) ?? 0) > now || this.movement.sample(player.id, now)?.walking)).map((player) => player.id);
  }

  private presentationAt(now: number): PresentedPlayer[] {
    if (!this.state) return [];
    const movementVisible = !this.shot || now - this.shot.started >= this.shot.result.duration;
    return combatPlayers(this.shot?.previous ?? null, this.state, this.shot?.result ?? null, this.shot ? now - this.shot.started : 0).map((player) => {
      const movement = movementVisible ? this.movement.sample(player.id, now) : null;
      if (!movement || (!player.alive && movement.hpBeforeDeath === undefined)) {
        const deathPose = !player.alive ? this.deaths.pose(player.id) : null;
        return deathPose ? { ...player, ...deathPose } : player;
      }
      return { ...player, x: movement.x, y: movement.y, face: movement.face,
        ...(movement.hpBeforeDeath !== undefined ? { alive: true, hp: movement.hpBeforeDeath, renderAlive: true, deathProgress: 0 } : {}) };
    });
  }

  private async binaryMask(path: string): Promise<HTMLCanvasElement> {
    if (!this.masks.has(path)) {
      const loaded = this.assetLoader.terrainReady(path).then((terrain) => {
        if (!terrain) throw new Error('Missing destruction mask');
        this.collisionMasks.set(path, terrain);
        const canvas = document.createElement('canvas'); canvas.width = terrain.width; canvas.height = terrain.height;
        const ctx = canvas.getContext('2d')!;
        const pixels = ctx.createImageData(terrain.width, terrain.height);
        for (let y = 0; y < terrain.height; y++) for (let x = 0; x < terrain.width; x++) {
          if (terrain.data[y * terrain.stride + (x >> 3)] & (0x80 >> (x & 7))) pixels.data[(y * terrain.width + x) * 4 + 3] = 255;
        }
        ctx.putImageData(pixels, 0, 0);
        return canvas;
      }).catch((error) => { this.masks.delete(path); throw error; });
      this.masks.set(path, loaded);
    }
    return this.masks.get(path)!;
  }

  private async prepareTerrain() {
    if (!this.map || !this.state) return;
    const map = this.map;
    let craters = this.state.craters;
    if (this.shot) {
      const elapsed = performance.now() - this.shot.started;
      // Engine appends operations in impact order. Preserve the exact history,
      // including repeated holes at identical coordinates, then reveal this
      // shot's operations one by one when their authoritative impact arrives.
      const diggingImpacts = this.shot.result.impacts.filter((impact) => impact.craterId);
      craters = [...this.shot.historyCraters, ...this.shot.result.craters.filter((_, index) => elapsed >= (diggingImpacts[index]?.at ?? this.shot!.result.duration))];
    }
    const version = `${map.id}:${JSON.stringify(craters)}`;
    if (version === this.terrainVersion) { void this.prepareCollisionTerrain(map, craters, version); return; }
    this.terrainVersion = version;
    const foreground = await this.loadImage(map.foreground);
    if (!foreground || version !== this.terrainVersion) return;
    const scratch = document.createElement('canvas'); scratch.width = map.width; scratch.height = map.height;
    const ctx = scratch.getContext('2d')!;
    drawLogicalImage(ctx, foreground, 0, 0, map.width, map.height);
    for (const crater of craters) {
      let definition: BulletAsset | undefined;
      for (const w of this.assets.weapons) for (const ball of [w.normal, w.special]) {
        if (String(ball?.craterId) === String(crater.id)) definition = ball;
      }
      if (!definition?.craterMask) continue;
      try {
        const mask = await this.binaryMask(definition.craterMask);
        const border = definition.crater ? await this.loadImage(definition.crater) : null;
        const timeline = definition.craterTimeline as { anchor?: Point } | undefined;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        drawLogicalImage(ctx, mask, Math.round(crater.x) - Math.floor(mask.width / 2), Math.round(crater.y) - Math.floor(mask.height / 2));
        if (border) {
          const anchor = timeline?.anchor ?? { x: logicalImageSize(border).width / 2, y: logicalImageSize(border).height / 2 };
          // The recovered SWF border keeps its native pivot and pixel size.
          // source-atop burns only surviving terrain, never transparent air.
          ctx.globalCompositeOperation = 'source-atop';
          drawLogicalImage(ctx, border, Math.round(crater.x) - anchor.x, Math.round(crater.y) - anchor.y);
        }
        ctx.restore();
      } catch { /* Retain the original foreground until its authoritative mask loads. */ }
    }
    if (version !== this.terrainVersion) return;
    this.terrain = scratch;
    this.terrainReadyVersion = version;
    this.visibleCraterCount = craters.length;
    this.updateLoading();
    void this.prepareCollisionTerrain(map, craters, version);
  }

  private async prepareCollisionTerrain(map: MapAsset, craters: CraterOperation[], version: string) {
    if (!map.mask) return;
    if (version === this.collisionVersion && this.collisionTerrain) {
      if (!this.movement.hasTerrain) this.movement.setTerrain(this.collisionTerrain);
      return;
    }
    if (!this.collisionBases.has(map.mask)) this.collisionBases.set(map.mask, this.assetLoader.terrainReady(map.mask).then((terrain) => {
      if (!terrain) throw new Error('Missing terrain collision asset');
      return terrain;
    }).catch((error) => { this.collisionBases.delete(map.mask!); throw error; }));
    try {
      const terrain = (await this.collisionBases.get(map.mask)!).clone();
      const masks = new Map<string, Terrain>();
      for (const crater of craters) {
        let definition: BulletAsset | undefined;
        for (const weapon of this.assets.weapons) for (const ball of [weapon.normal, weapon.special]) {
          if (String(ball.craterId) === String(crater.id)) definition = ball;
        }
        if (!definition?.craterMask) return;
        await this.binaryMask(definition.craterMask);
        const mask = this.collisionMasks.get(definition.craterMask);
        if (!mask) return;
        masks.set(String(crater.id), mask);
      }
      terrain.replay(craters, masks);
      if (version !== this.terrainVersion) return;
      this.collisionVersion = version;
      this.collisionTerrain = terrain;
      this.movement.setTerrain(terrain);
    } catch { /* Authority still moves the character if a preview mask fails. */ }
  }

  private shotBall(kind: ShotResult['kind'], weapon: WeaponAsset, special: boolean): BulletAsset {
    if (kind === 'fly' || kind === 'ice') return this.assets.actions?.[kind] ?? (special ? weapon.special : weapon.normal);
    return special ? weapon.special : weapon.normal;
  }

  private weaponMovie(weapon: WeaponAsset, kind: ShotResult['kind']): WeaponMovieAsset | null {
    return kind === 'fly' || kind === 'ice' ? attackAssets.actions[kind] : attackAssets.weapons[String(weapon.id)]?.[kind === 'special' ? 'special' : 'normal'] ?? null;
  }

  private startShot(result: ShotResult, previous: GameState | null) {
    if (this.renderedShots.has(result.id)) return;
    this.renderedShots.add(result.id);
    const elapsed = Math.max(0, Date.now() + this.clockOffset - result.createdAt);
    const weapon = this.assets.weapons.find((w) => String(w.id) === String(result.weaponId));
    if (!weapon) return;
    const observedAt = performance.now();
    const localStartedAt = this.characterAnimation.confirmShot(result, observedAt);
    const firstLaunch = volleyLaunches(result)[0];
    const localClock = localStartedAt === undefined ? undefined : { authorityElapsedAtAck: elapsed, localElapsedAtAck: observedAt - localStartedAt };
    const bullets: RenderProjectile[] = result.trajectories.map((entry) => ({ ...entry, weapon, special: result.special,
      localClock: firstLaunch !== undefined && entry.launchDelay - firstLaunch <= 180 ? localClock : undefined }));
    const ball = this.shotBall(result.kind, weapon, result.special);
    const timeline = ball.explosionTimeline as { frameRate?: number } | undefined;
    const effectDuration = ball.explosion.length * 1000 / (timeline?.frameRate ?? 25);
    const duration = Math.max(result.duration, attackPresentationEnd(result, this.weaponMovie(weapon, result.kind)?.phases.end.durationMs),
      ...result.impacts.map((impact) => impact.at + effectDuration));
    if (!bullets.length || elapsed >= duration) return;
    // Translate the authoritative epoch time into the local animation clock.
    const historyCraters = this.state?.craters.slice(0, Math.max(0, this.state.craters.length - result.craters.length)) ?? [];
    this.shot = { id: result.id, started: observedAt - elapsed, duration, bullets, previous: previous?.phase === 'playing' ? previous : null, result, historyCraters };
    // User-selected minimap/full-map views keep their control during a shot.
    if (this.coreReady) for (const entry of bullets) { for (const path of entry.projectile) this.image(path); for (const path of entry.explosion) this.image(path); }
  }

  private animate(now: number) {
    this.draw(now);
    this.frame = requestAnimationFrame(this.animate);
  }

  private draw(now: number) {
    const ctx = this.ctx;
    const width = this.canvas.width; const height = this.canvas.height;
    ctx.clearRect(0, 0, width, height);
    if (!this.map || !this.state || !this.coreReady) return;
    const map = this.map;
    const fit = Math.min(width / map.width, height / map.height);
    this.camera.scale = this.camera.fullMap ? fit : width / 1100 * this.camera.zoom;
    const elapsed = this.shot ? now - this.shot.started : 0;
    if (this.shot && elapsed > this.shot.duration) { this.shot = null; void this.prepareTerrain(); }
    if (this.shot) void this.prepareTerrain();
    const frameTime = this.lastCameraFrame ? Math.max(0, Math.min(64, now - this.lastCameraFrame)) : 1000 / 60;
    this.lastCameraFrame = now;
    this.presentedTurnId = cameraTurnPlayerId(this.state, this.presentedTurnId, Date.now() + this.clockOffset);
    const presentedPlayers = this.presentationAt(now);
    if (!this.camera.manual && !this.camera.fullMap) {
      // The next player is already in the authoritative state during a void
      // fall. Keep following the old visible body until its source action ends.
      const activePose = presentedPlayers.find((p) => p.id === this.presentedTurnId);
      let target = activePose ? sourcePlayerFocus(activePose) : { x: map.width / 2, y: map.height / 2 };
      const bullet = this.shot?.bullets.find((b) => projectilePresentationElapsed(elapsed, b.launchDelay, b.points.at(-1)?.t ?? b.launchDelay, b.localClock) >= b.launchDelay
        && elapsed < (b.points.at(-1)?.t ?? 0));
      const settling = this.shot && elapsed < this.shot.result.duration;
      if (bullet) target = this.sample(bullet, projectilePresentationElapsed(elapsed, bullet.launchDelay, bullet.points.at(-1)?.t ?? bullet.launchDelay, bullet.localClock));
      else if (settling) {
        // Keep the last impact in view for damage/falling, instead of racing
        // toward the server's already-selected next character mid-explosion.
        const landed = this.shot!.bullets.filter((entry) => (entry.points.at(-1)?.t ?? Infinity) <= elapsed)
          .sort((a, b) => (b.points.at(-1)?.t ?? 0) - (a.points.at(-1)?.t ?? 0))[0];
        if (landed) target = landed.impact ?? landed.points.at(-1) ?? target;
        else {
          const firing = this.shot!.previous?.players.find((entry) => entry.id === this.shot!.result.playerId);
          if (firing) target = sourcePlayerFocus(firing);
        }
      }
      const transition = this.cameraTransition;
      if (!settling && transition && now >= transition.startsAt) {
        transition.from ??= { x: this.camera.x, y: this.camera.y };
        const fraction = Math.max(0, Math.min(1, (now - transition.startsAt) / transition.duration));
        const eased = fraction * fraction * (3 - 2 * fraction);
        const destination = this.clampCameraTarget(target, width, height);
        this.camera.x = transition.from.x + (destination.x - transition.from.x) * eased;
        this.camera.y = transition.from.y + (destination.y - transition.from.y) * eased;
        if (fraction >= 1) this.cameraTransition = null;
      } else {
        // Exponential smoothing behaves equally at 30/60/120 Hz.
        const smoothing = 1 - Math.exp(-frameTime / (this.reducedMotion ? 70 : 170));
        this.camera.x += (target.x - this.camera.x) * smoothing;
        this.camera.y += (target.y - this.camera.y) * smoothing;
      }
    }
    const bounded = this.clampCameraTarget(this.camera, width, height);
    this.camera.x = bounded.x; this.camera.y = bounded.y;
    const left = width / 2 - this.camera.x * this.camera.scale;
    const top = height / 2 - this.camera.y * this.camera.scale;
    const bg = this.image(map.background);
    if (bg) {
      const backdrop = backgroundViewport({ width, height }, logicalImageSize(bg), map, this.camera, width / 1100);
      drawLogicalImage(ctx, bg, backdrop.x, backdrop.y, backdrop.width, backdrop.height);
    }
    // Terrain, actors and projectiles retain the authoritative world camera.
    ctx.save();
    ctx.translate(left, top); ctx.scale(this.camera.scale, this.camera.scale);
    const foreground = this.image(map.foreground);
    if (this.terrainReadyVersion && this.terrain.width === map.width) drawLogicalImage(ctx, this.terrain, 0, 0);
    else if (foreground) drawLogicalImage(ctx, foreground, 0, 0, map.width, map.height);
    if (this.shot) for (const [index, damage] of this.shot.result.damage.entries()) {
      const at = damageEventAt(damage, this.shot.result), key = `${this.shot.id}:${index}`;
      if (elapsed < at || this.shownDamage.has(key)) continue;
      this.shownDamage.add(key);
      const target = presentedPlayers.find((player) => player.id === damage.playerId);
      if (damage.amount > 0 && !damage.killed && target?.alive) this.characterAnimation.hurt(damage.playerId, this.shot.started + at);
    }
    this.propEffects = this.propEffects.filter((effect) => now - effect.startedAt < Math.max(
      feedbackAssets.effects.propAura.frames.length * 1000 / feedbackAssets.effects.propAura.frameRate,
      feedbackAssets.effects.propAura.headIconAtSourceFrame * 40 + 1640));
    this.deaths.retain(presentedPlayers);
    for (const id of this.deathFrames.keys()) if (!presentedPlayers.some((player) => player.id === id && !player.alive)) this.deathFrames.delete(id);
    const dead = presentedPlayers.filter((player) => !player.alive);
    for (const player of dead) {
      const at = this.shot ? deathEventAt(player.id, this.shot.result) : null;
      const startedAt = at === null || !this.shot ? this.movement.deathStartedAt(player.id) ?? now : this.shot.started + at;
      const pose = this.presentationAt(startedAt).find((entry) => entry.id === player.id) ?? player;
      this.deaths.ensure(pose, startedAt, { gravity: Number(map.gravity ?? 9.8), drag: Number(map.drag ?? 2),
        wind: at === null ? this.state.turn.wind : this.shot?.previous?.turn.wind ?? this.state.turn.wind });
    }
    // Wait for this frame's authoritative crater mask before restarting a
    // settled tomb; the first physics tick must see the newly removed support.
    if (this.shot && (!map.mask || this.collisionVersion === this.terrainVersion)) {
      for (const [index, impact] of this.shot.result.impacts.entries()) {
        const key = `${this.shot.id}:${index}`;
        if (elapsed < impact.at || this.shownImpacts.has(key)) continue;
        this.shownImpacts.add(key);
        this.deaths.blast(impact, this.shot.started + impact.at, this.collisionTerrain);
      }
    }
    for (const player of dead) {
      const death = this.deaths.sampleVisual(player.id, now, this.collisionTerrain);
      if (death?.tomb.visible) {
        const tomb = this.image(deathAssets.effects.tomb.path), rules = deathAssets.sourceRules.tomb;
        ctx.save(); ctx.translate(death.tomb.x + rules.spritePosition.x, death.tomb.y + rules.spritePosition.y);
        ctx.rotate(death.tomb.angle * Math.PI / 180);
        if (tomb) drawLogicalImage(ctx, tomb, rules.bitmapPosition.x, rules.bitmapPosition.y);
        ctx.restore();
      }
    }
    for (const player of presentedPlayers) if (player.alive) this.drawPlayer(player, now, !this.shot || elapsed >= this.shot.result.duration);
    // TombView is inserted at the bottom of the living layer; the soul is
    // brought to its top, so other characters must not cover the ghost body.
    for (const player of dead) this.drawDeadPlayer(player, now);
    if (this.shot) this.drawShot(this.shot, elapsed);
    ctx.restore();
    this.stateNeedsPhysicsFrame = false;
    this.drawMinimap(); this.lastMinimapFrame = now;
  }

  private clampCameraTarget(target: Point, width: number, height: number): Point {
    const map = this.map!;
    const halfW = width / this.camera.scale / 2; const halfH = height / this.camera.scale / 2;
    return {
      x: halfW >= map.width / 2 ? map.width / 2 : Math.min(map.width - halfW, Math.max(halfW, target.x)),
      y: halfH >= map.height / 2 ? map.height / 2 : Math.min(map.height - halfH, Math.max(halfH, target.y)),
    };
  }

  private drawFrame(paths: string[], elapsed: number, rate: number, loop: boolean, x: number, y: number, anchor?: Point): SourceFrameBlend {
    const sample = sourceFrameBlend(elapsed, rate, paths.length, loop);
    const current = this.image(paths[sample.frameIndex]), next = this.image(paths[sample.nextFrameIndex]);
    const sprite = (image: HTMLImageElement | null) => {
      if (!image) return null;
      const size = logicalImageSize(image), pivot = anchor ?? { x: size.width / 2, y: size.height / 2 };
      return { image, destination: { x: x - pivot.x, y: y - pivot.y, ...size } };
    };
    this.spriteBlend.draw(this.ctx, sprite(current), sprite(next), sample.mix);
    return sample;
  }

  private drawPlayer(player: PresentedPlayer, now: number, presentMovement = true) {
    if (!player.renderAlive) { this.characterAnimation.cancelFeedback(player.id); this.characterFrames.delete(player.id); this.feedbackFrames.delete(player.id); return; }
    if (!player.alive) this.characterAnimation.cancelFeedback(player.id);
    const movement = presentMovement ? this.movement.sample(player.id, now) : null;
    if (movement) player = { ...player, x: movement.x, y: movement.y, face: movement.face };
    const ctx = this.ctx;
    const character = this.assets.characters.find((c) => String(c.id) === String((player as any).characterId)) ?? this.assets.characters[player.team === 1 ? 1 : 0] ?? this.assets.characters[0];
    if (!character) return;
    const walking = presentMovement && !movement?.falling && ((this.walking.get(player.id) ?? 0) > now || Boolean(movement?.walking));
    if (movement?.falling) this.characterAnimation.cancelFeedback(player.id);
    const action = this.characterAnimation.sample(character, player.id, now, walking, this.shot ? { result: this.shot.result, startedAt: this.shot.started } : undefined,
      (weaponId, kind) => {
        const weapon = this.assets.weapons.find((entry) => String(entry.id) === weaponId);
        return weapon ? this.weaponMovie(weapon, kind)?.phases.end.durationMs ?? 0 : 0;
      });
    const weapon = this.assets.weapons.find((entry) => String(entry.id) === (action.weaponId ?? String(player.weaponId)));
    const movie = weapon && action.kind ? this.weaponMovie(weapon, action.kind) : null;
    let weaponMovieFrame: { path: string; nextPath: string; phase: 'start' | 'shot' | 'end'; frameIndex: number; nextFrameIndex: number; mix: number; sourceFrame: number; loaded: boolean } | null = null;
    const actionImage = this.image(action.path);
    const image = actionImage ?? this.image(Array.isArray(character.idle) ? character.idle[0] : character.idle);
    const nextImage = actionImage ? this.image(action.nextPath) : image;
    const held = this.image(weapon?.held);
    const heldSheet = this.image(weapon?.heldSheet);
    const anchor = character.anchor ?? { x: 62, y: 83 };
    const heldAnchor = weapon?.heldAnchor ?? character.weaponAnchor ?? anchor;
    const bodyAngle = this.collisionTerrain ? terrainBodyAngle(this.collisionTerrain, player) : 0;
    ctx.save(); ctx.translate(player.x, player.y - 3); ctx.rotate(bodyAngle * Math.PI / 180);
    ctx.globalAlpha = 1 - player.deathProgress;
    if (player.face < 0) ctx.scale(-1, 1);
    if (heldSheet) {
      const columns = character.sheetColumns ?? 13;
      const sourceFrame = actionImage ? action.sourceFrame : 0;
      const nextSourceFrame = actionImage ? action.nextSourceFrame : 0;
      const heldFrame = (frame: number) => ({ image: heldSheet,
        source: { x: frame % columns * character.width, y: Math.floor(frame / columns) * character.height, width: character.width, height: character.height },
        destination: { x: -heldAnchor.x, y: -heldAnchor.y, width: character.width, height: character.height } });
      this.spriteBlend.draw(ctx, heldFrame(sourceFrame), heldFrame(nextSourceFrame), action.mix);
    } else if (held) drawLogicalImage(ctx, held, -heldAnchor.x, -heldAnchor.y, character.width, character.height);
    const bodyFrame = (body: HTMLImageElement | null) => body ? { image: body,
      destination: { x: -anchor.x, y: -anchor.y, width: character.width, height: character.height } } : null;
    this.spriteBlend.draw(ctx, bodyFrame(image), bodyFrame(nextImage), action.mix);
    if (movie && action.weaponPhase) {
      const phase = movie.phases[action.weaponPhase];
      const sample = this.drawFrame(phase.frames, action.weaponPhaseElapsed, movie.frameRate, false,
        movie.position.x, movie.position.y, movie.anchor);
      const path = phase.frames[sample.frameIndex];
      weaponMovieFrame = { path, nextPath: phase.frames[sample.nextFrameIndex], phase: action.weaponPhase,
        ...sample, sourceFrame: phase.sourceFrames[sample.frameIndex], loaded: Boolean(this.image(path)) };
      // GamePlayer adds WeaponMovie after the body, at the same weaponX/Y
      // origin. The source wrapper MATRIX is already included in this pivot.
    }
    const readyAura = feedbackAssets.effects.powReadyAura;
    let powFrame: number | null = null;
    let powSample: SourceFrameBlend | null = null;
    if (player.alive && player.anger >= 200) {
      if (!this.powClocks.has(player.id)) this.powClocks.set(player.id, now);
      // Dander is a child of _player (scale−direction), rather than _body
      // (the second mirror that cancels that scale for character textures).
      ctx.save(); ctx.scale(-1, 1);
      powSample = this.drawFrame(readyAura.frames, now - this.powClocks.get(player.id)!, readyAura.frameRate, true,
        readyAura.position.x, readyAura.position.y, readyAura.anchor);
      powFrame = powSample.frameIndex; ctx.restore();
    } else this.powClocks.delete(player.id);
    this.characterFrames.set(player.id, { action: action.action, frameIndex: action.frameIndex, sourceFrame: action.sourceFrame, key: action.key,
      nextFrameIndex: action.nextFrameIndex, nextSourceFrame: action.nextSourceFrame, mix: action.mix,
      startedAt: action.startedAt, weaponPhaseElapsed: action.weaponPhaseElapsed,
      bodyAngle, muzzle: sourceBodyPoint(player, bodyAngle), pose: { x: player.x, y: player.y }, falling: Boolean(movement?.falling),
      weaponId: action.weaponId, kind: action.kind, weaponMovie: weaponMovieFrame });
    ctx.restore();
    const freeze = feedbackAssets.effects.frozenBlock;
    let frozenFrame: number | null = null;
    let frozenSample: SourceFrameBlend | null = null;
    if (player.alive && player.frozenTurns > 0) {
      if (!this.frozenClocks.has(player.id)) this.frozenClocks.set(player.id, now);
      // GameLiving adds FrostEffect outside _player; it is upright, with no
      // facing mirror or body slope, and appears only on timed frozen state.
      frozenSample = this.drawFrame(freeze.frames, now - this.frozenClocks.get(player.id)!, freeze.frameRate, true,
        player.x + freeze.position.x, player.y + freeze.position.y, freeze.anchor);
      frozenFrame = frozenSample.frameIndex;
    } else this.frozenClocks.delete(player.id);
    const propAura = feedbackAssets.effects.propAura, propFrames: number[] = [];
    const propSamples: SourceFrameBlend[] = [];
    for (const effect of this.propEffects.filter((entry) => entry.playerId === player.id)) {
      const elapsed = now - effect.startedAt;
      if (elapsed >= 0 && elapsed < propAura.frames.length * 1000 / propAura.frameRate) {
        const sample = this.drawFrame(propAura.frames, elapsed, propAura.frameRate, false,
          player.x + propAura.position.x, player.y + propAura.position.y, propAura.anchor);
        propFrames.push(sample.frameIndex); propSamples.push(sample);
      }
      const iconAge = (now - effect.startedAt) * propAura.frameRate / 1000 - propAura.headIconAtSourceFrame;
      const icon = effect.icon ? this.image(effect.icon) : null;
      if (icon && iconAge >= 0 && iconAge <= 40) {
        // AutoPropEffect advances age by.2 on each25fps tick and positions
        // its actual60×60 prop image at localx−20 inside(-5,-140).
        const alphaAt = (tick: number) => {
          const age = tick * .2;
          return age <= 1 ? age : age < 4 ? 1 : age < 5 ? Math.max(.2, 5 - age) : 1;
        };
        const lower = Math.floor(iconAge), fraction = iconAge - lower;
        ctx.save(); ctx.globalAlpha = alphaAt(lower) + (alphaAt(lower + 1) - alphaAt(lower)) * fraction;
        drawLogicalImage(ctx, icon, player.x + propAura.headIconPosition.x - 20, player.y + propAura.headIconPosition.y, 60, 60); ctx.restore();
      }
    }
    this.feedbackFrames.set(player.id, { propAura: propFrames, powReadyAura: powFrame, frozenBlock: frozenFrame,
      interpolation: { propAura: propSamples, powReadyAura: powSample, frozenBlock: frozenSample } });
    const active = this.presentedTurnId === player.id;
    ctx.font = 'bold 12px SimSun, "宋体", Arial, sans-serif'; ctx.textAlign = 'center';
    ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(38,25,17,.8)'; ctx.fillStyle = player.team === 0 ? '#ffbdab' : '#c1e7ff';
    ctx.strokeText(player.name, player.x, player.y - 99); ctx.fillText(player.name, player.x, player.y - 99);
    const hpBack = this.image(this.assets.ui?.smallHpBack);
    const hpStrip = this.image(this.assets.ui?.[player.team === 0 ? 'smallHpRed' : 'smallHpBlue']);
    const fraction = Math.max(0, Math.min(1, player.hp / player.maxHp));
    if (hpBack) drawLogicalImage(ctx, hpBack, player.x - logicalImageSize(hpBack).width / 2, player.y - 92);
    if (hpStrip && fraction > 0) {
      const amount = Math.floor(logicalImageSize(hpStrip).width * fraction);
      if (amount) drawLogicalImage(ctx, hpStrip, 0, 0, amount, logicalImageSize(hpStrip).height, player.x - logicalImageSize(hpStrip).width / 2, player.y - 91, amount, logicalImageSize(hpStrip).height);
    }
    if (active) {
      ctx.fillStyle = '#ffe773'; ctx.beginPath(); ctx.moveTo(player.x - 5, player.y - 117); ctx.lineTo(player.x + 5, player.y - 117); ctx.lineTo(player.x, player.y - 110); ctx.fill();
      if (player.id === this.localPlayerId && player.alive && this.state?.phase === 'playing'
        && this.state.turn.playerId === player.id && Date.now() + this.clockOffset >= this.state.turn.readyAt
        && (!this.shot || now - this.shot.started >= this.shot.result.duration) && !this.characterAnimation.awaitingShot) this.drawAim(player, now);
    }
  }

  private drawDeadPlayer(player: PresentedPlayer, now: number) {
    this.characterAnimation.cancelFeedback(player.id); this.characterAnimation.cancelPreview(player.id);
    this.characterAnimation.cancelPreparation(player.id); this.characterFrames.delete(player.id); this.feedbackFrames.delete(player.id);
    this.powClocks.delete(player.id); this.frozenClocks.delete(player.id);
    this.propEffects = this.propEffects.filter((effect) => effect.playerId !== player.id);
    const death = this.deaths.sampleVisual(player.id, now, this.collisionTerrain); if (!death) return;
    const character = this.assets.characters.find((entry) => String(entry.id) === String((player as any).characterId))
      ?? this.assets.characters[player.team === 1 ? 1 : 0] ?? this.assets.characters[0];
    const characterId = String(character?.id) as keyof typeof deathAssets.characters;
    const spec = deathAssets.characters[characterId]; if (!spec) return;
    const ghost = deathAssets.effects[spec.ghostEffect as 'maleGhost' | 'femaleGhost'], shine = deathAssets.effects.ghostShine;
    const ghostFrame = deathLoopFrame(death.elapsed, ghost.frames.length, ghost.frameRate);
    const shineFrame = deathLoopFrame(death.elapsed, shine.frames.length, shine.frameRate);
    const soulPath = spec.soul[death.soulFrame];
    const ctx = this.ctx;
    ctx.save(); ctx.translate(death.soul.x, death.soul.y); ctx.scale(death.soul.face, 1);
    // Preserve source glow color/blur and native pixels. Canvas shadowBlur is
    // an approximation of Flash's group GlowFilter; see the source audit.
    const glow = deathAssets.sourceRules.glowFilter;
    ctx.shadowColor = `#${glow.color.toString(16).padStart(6, '0')}`; ctx.shadowBlur = glow.blurX * this.camera.scale;
    const soulSprite = (frame: number) => {
      const image = this.image(spec.soul[frame]);
      return image ? { image, destination: { x: -spec.anchor.x, y: -spec.anchor.y, ...logicalImageSize(image) } } : null;
    };
    this.spriteBlend.draw(ctx, soulSprite(death.soulFrame), soulSprite(death.nextSoulFrame), death.soulMix);
    this.drawFrame(ghost.frames, death.elapsed, ghost.frameRate, true, ghost.position.x, ghost.position.y, ghost.anchor);
    this.drawFrame(shine.frames, death.elapsed, shine.frameRate, true, shine.position.x, shine.position.y, shine.anchor);
    ctx.restore();
    ctx.font = 'bold 12px SimSun, "宋体", Arial, sans-serif'; ctx.textAlign = 'center';
    ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(38,25,17,.8)'; ctx.fillStyle = player.team === 0 ? '#ffbdab' : '#c1e7ff';
    const pose = this.deaths.pose(player.id) ?? player;
    const nameY = pose.y + deathAssets.sourceRules.soulRootOffset.y - 99 + 10;
    ctx.strokeText(player.name, pose.x, nameY); ctx.fillText(player.name, pose.x, nameY);
    this.deathFrames.set(player.id, { ...death, characterId, soulPath, ghostFrame, shineFrame });
  }

  private drawAim(player: PlayerState, now: number) {
    const background = this.image(classicAim.assets.background.path);
    const hand = this.image(classicAim.assets.hand.path);
    if (!background || !hand) return;
    const ctx = this.ctx;
    const angle = this.aim?.playerId === player.id ? this.aimAngleAt(now) : player.angle;
    const bodyAngle = this.collisionTerrain ? terrainBodyAngle(this.collisionTerrain, player) : 0;
    ctx.save(); ctx.translate(player.x, player.y - 3); ctx.rotate(bodyAngle * Math.PI / 180);
    // The original TakeAimAsset hand points left. Mirroring the entire source
    // component puts its (-30,-20) local origin in front of either facing.
    ctx.scale(-player.face, 1); ctx.translate(classicAim.playerOffset.x, classicAim.playerOffset.y);
    const back = classicAim.assets.background;
    drawLogicalImage(ctx, background, back.placement.x - back.anchor.x, back.placement.y - back.anchor.y);
    const cursor = classicAim.assets.hand;
    ctx.translate(cursor.placement.x, cursor.placement.y); ctx.rotate(angle * Math.PI / 180);
    drawLogicalImage(ctx, hand, -cursor.anchor.x, -cursor.anchor.y); ctx.restore();
  }

  private sample(bullet: RenderProjectile, time: number): Point {
    if (!bullet.points.length) return bullet.impact ?? { x: 0, y: 0 };
    let low = 0; let high = bullet.points.length - 1;
    while (low < high) { const mid = Math.ceil((low + high) / 2); if (bullet.points[mid].t <= time) low = mid; else high = mid - 1; }
    const a = bullet.points[low]; const b = bullet.points[Math.min(low + 1, bullet.points.length - 1)];
    const fraction = b.t === a.t ? 0 : Math.max(0, Math.min(1, (time - a.t) / (b.t - a.t)));
    return { x: a.x + (b.x - a.x) * fraction, y: a.y + (b.y - a.y) * fraction };
  }

  private drawShot(shot: ShotAnimation, elapsed: number) {
    const ctx = this.ctx;
    for (const bullet of shot.bullets) {
      const impactAt = bullet.points.at(-1)?.t ?? bullet.launchDelay;
      const visualElapsed = projectilePresentationElapsed(elapsed, bullet.launchDelay, impactAt, bullet.localClock);
      if (visualElapsed < bullet.launchDelay) continue;
      const spec = this.shotBall(shot.result.kind, bullet.weapon, bullet.special);
      const projectileTimeline = spec.projectileTimeline as { anchor?: Point; frameRate?: number; loop?: boolean } | undefined;
      const explosionTimeline = spec.explosionTimeline as { anchor?: Point; frameRate?: number; loop?: boolean } | undefined;
      if (elapsed < impactAt) {
        const point = this.sample(bullet, visualElapsed);
        const loop = projectileTimeline?.loop || String(bullet.weapon.id) === 'electricbar';
        if (bullet.projectile.length) {
          ctx.save(); ctx.translate(point.x, point.y);
          const heading = projectileHeading(bullet.points, visualElapsed);
          const launchDirection = bullet.points.length > 1 && bullet.points[1].x < bullet.points[0].x ? -1 : 1;
          // Bomb3's paper-plane movie already has the source flight alignment.
          // SimpleBomb uses motionAngle and the launch-direction Y flip only.
          const reversedSource = shot.result.kind === 'ice'
            || (shot.result.kind !== 'fly' && String(bullet.weapon.id) === 'electricbar');
          const rotationOffset = reversedSource ? Math.PI : Number(spec.rotationOffset ?? 0);
          ctx.rotate(heading + rotationOffset + (bullet.spin ? (visualElapsed - bullet.launchDelay) / 40 * bullet.spin * launchDirection * Math.PI / 180 : 0));
          if (bullet.spin) ctx.scale(launchDirection, 1); else ctx.scale(1, launchDirection);
          this.drawFrame(bullet.projectile, visualElapsed - bullet.launchDelay, projectileTimeline?.frameRate ?? 25,
            Boolean(loop), 0, 0, projectileTimeline?.anchor); ctx.restore();
        }
      } else if (bullet.impact) {
        const frame = Math.floor((elapsed - impactAt) * (explosionTimeline?.frameRate ?? 25) / 1000);
        // Original empty/held frames are part of the timeline; never loop it.
        if (frame < bullet.explosion.length) {
          this.drawFrame(bullet.explosion, elapsed - impactAt, explosionTimeline?.frameRate ?? 25, false,
            bullet.impact.x, bullet.impact.y, explosionTimeline?.anchor);
        }
      }
    }
    if (shot.result.damage.length) {
      ctx.save(); ctx.font = 'bold 25px SimSun, "宋体", Arial, sans-serif'; ctx.textAlign = 'center'; ctx.lineWidth = 3;
      for (const damage of shot.result.damage) {
        const damageTime = elapsed - damageEventAt(damage, shot.result);
        if (damageTime < 0 || damageTime >= 1000) continue;
        const player = this.presentationAt(shot.started + damageEventAt(damage, shot.result)).find((p) => p.id === damage.playerId);
        if (!player) continue;
        const label = damage.frozen ? '冰冻' : damage.thawed ? '解冻' : damage.amount ? `${damage.critical ? '暴击 ' : ''}−${damage.amount}` : damage.killed ? '出局' : '';
        ctx.globalAlpha = Math.max(0, 1 - damageTime / 1000); ctx.strokeStyle = '#682e20'; ctx.fillStyle = damage.frozen ? '#d5efff' : '#ffdf8c';
        ctx.strokeText(label, player.x, player.y - 115 - damageTime / 35); ctx.fillText(label, player.x, player.y - 115 - damageTime / 35);
      }
      ctx.restore();
    }
  }

  private drawMinimap() {
    if (!this.map || !this.state) return;
    const ctx = this.miniCtx; const width = this.minimap.width; const height = this.minimap.height;
    ctx.clearRect(0, 0, width, height);
    const bg = this.image(this.map.background);
    const terrain = this.terrainReadyVersion ? this.terrain : null;
    if (this.miniBaseMap !== this.map || this.miniBaseTerrain !== terrain || this.miniBaseTerrainVersion !== this.terrainReadyVersion || this.miniBaseBackground !== bg
      || this.miniBase.width !== width || this.miniBase.height !== height) {
      this.miniBase.width = width; this.miniBase.height = height;
      const base = this.miniBase.getContext('2d')!;
      if (bg) drawLogicalImage(base, bg, 0, 0, width, height);
      if (this.terrainReadyVersion) drawLogicalImage(base, this.terrain, 0, 0, width, height);
      this.miniBaseMap = this.map; this.miniBaseBackground = bg; this.miniBaseTerrain = terrain;
      this.miniBaseTerrainVersion = this.terrainReadyVersion;
      this.miniBaseBuilds++;
    }
    drawLogicalImage(ctx, this.miniBase, 0, 0);
    const sx = width / this.map.width; const sy = height / this.map.height;
    for (const player of this.presentedPlayers) {
      if (!player.alive) continue;
      ctx.fillStyle = player.team === 0 ? '#f5896e' : '#8ccfff';
      ctx.strokeStyle = '#392418'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(player.x * sx, player.y * sy, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    }
    ctx.strokeStyle = '#fff4bb'; ctx.lineWidth = 1;
    const vw = this.canvas.width / this.camera.scale * sx; const vh = this.canvas.height / this.camera.scale * sy;
    ctx.strokeRect(this.camera.x * sx - vw / 2, this.camera.y * sy - vh / 2, vw, vh);
  }

  destroy() {
    this.coreGeneration++; cancelAnimationFrame(this.frame); this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.handleResize); this.movement.reset(); this.characterAnimation.reset(); this.characterFrames.clear();
    this.minimap.removeEventListener('pointerdown', this.handleMapPointer);
    this.canvas.removeEventListener('wheel', this.handleWorldWheel);
    this.deaths.reset(); this.deathFrames.clear();
  }
}
